<template>
  <div class="container analytics">
    <section id="editContent" class="columnContainer">
      <div class="innerColumn">
        <h1 class="contentTitle">{{locale.analytics}}</h1>
        <Search
          :locale="locale"
          :filter="filterData"
          @updateStartDate="startDate"
          @updateEndDate="endDate"
          @search="search" />
        <p class="sysMessage">{{locale.message_analytics_pulbic}}</p>
        <Visitor
          :locale="locale"
          :overall="overallData"
          :users="overallUsers"
          :views="overallViews"
          :days="pageViewDays"
          :hours="pageViewTime" />
        <PageView v-if="pageViewData"
          :locale="locale"
          :pageView="pageViewData"
          :filter="filterData"
          :productInfo="productInfo" />
      </div>
    </section>
  </div>
</template>

<script>
import moment from 'moment'
import regExp from '@/common/regExp'
import Search from '@/components/Analytics/Search.vue'
import PageView from '@/components/Analytics/PageView.vue'
import Visitor from '@/components/Analytics/Visitor.vue'

export default {
  name: 'Analytics',
  props: [
    'locale'
  ],
  components: {
    Search,
    PageView,
    Visitor
  },
  computed: {
    sitePlan () {
      return this.$store.getters['authentication/getAuthInfo'].sitePlan
    },
    admin () {
      return this.$route.params.admin
    },
    current () {
      const name = `admin_${this.admin}`
      return this.locale[name]
    }
  },
  watch: {
    locale (data) {
      this.overallUsers[0].name = data.analytics_pageview_users
      this.overallViews[0].name = data.analytics_pageview_views
      this.pageViewTime[0].name = data.time
      this.pageViewDays[0].name = data.day_of_the_week
    }
  },
  created () {
    this.getProductName()
    this.getAnalytics()
  },
  mounted () {
  },
  data () {
    return {
      productInfo: null,
      filterData: {
        startDate: null,
        endData: null
      },
      overallData: null,
      overallUsers: null,
      overallViews: null,
      pageViewData: null,
      pageViewTime: null,
      pageViewDays: null
    }
  },
  methods: {
    startDate (date) {
      this.filterData.startDate = date
    },
    endDate (date) {
      this.filterData.endDate = date
    },
    search (data) {
      this.getAnalytics(data.dateFrom, data.dateTo)
    },
    groupBy (objectArray, property, dataKey) {
      return objectArray.reduce(function (acc, obj) {
        let key = obj[property]
        if (!acc[key]) {
          acc[key] = {
            [dataKey]: []
          }
        }
        acc[key][dataKey].push(obj[[dataKey]])
        return acc
      }, {})
    },
    /**
     * pagePath가 product인 경우
     * pageTitle은 동일하나
     * 카테고리에 따라 path가 달라지기 때문에
     * 카테고리 표시를 함께 해줘야 함
     */
    async getProductName () {
      const productList = await this.$store.dispatch('product/getProductList')
      function isCategory (text) {
        return regExp.replaceAllEmpty(text).length > 0
      }
      if (productList) {
        const ids = productList.map(x => x.id)
        const item = await Promise.all(ids.map(id => this.$store.dispatch('product/getProductItem', id)))
        const getChildren = (cate) => {
          if (!(cate.children && cate.children.length)) {
            return cate
          }
          return [cate, this._.flatMapDeep(cate.children, getChildren)]
        }
        this.productInfo = item ? item.filter(x => {
          return x.category && x.category.length && x.category.find(x => x.label !== 'uncategorized')
        }).map(x => {
          return {
            id: x.id,
            ...x.category && x.category.length && {
              /**
               * category flatten all children
               * -> filter uncategorized
               * -> map category id + name
               */
              category: this._.flatMapDeep(x.category, getChildren).filter(c => isCategory(c.text)).map(c => {
                return {
                  id: c.data.id,
                  text: c.text && c.text.trim()
                }
              })
            }
          }
        }) : []
      }
    },
    /**
     * users data
     * merge with views data
     */
    setOverall (overallUsers, overallViews) {
      const usersData = overallUsers
        ? overallUsers.rows.map(x => {
          return {
            date: x.dimensionValues[0].value,
            users: parseInt(x.metricValues[0].value)
          }
        })
        : []
      const viewsData = overallViews
        ? overallViews.rows.map(x => {
          return {
            date: x.dimensionValues[0].value,
            views: parseInt(x.metricValues[0].value)
          }
        })
        : []
      const daysData = overallViews
        ? overallViews.rows.map(x => {
          return {
            dayOfWeek: x.dimensionValues[1].value,
            views: parseInt(x.metricValues[0].value)
          }
        })
        : []
      const usersGroupBy = this.groupBy(usersData, 'date', 'users')
      const viewsGroupBy = this.groupBy(viewsData, 'date', 'views')
      const daysGroupBy = this.groupBy(daysData, 'dayOfWeek', 'views')
      let usersResult = [
        {
          name: this.locale.analytics_pageview_users,
          data: []
        }
      ]
      let viewsResult = [
        {
          name: this.locale.analytics_pageview_views,
          data: []
        }
      ]
      let daysResult = [
        {
          name: this.locale.day_of_the_week,
          data: []
        }
      ]
      const dateList = getDate(this.filterData.startDate, this.filterData.endDate)
      function getDate (startDate, endDate) {
        let result = [moment(startDate).format('YYYYMMDD')]
        const currDate = moment(startDate)
        const lastDate = moment(endDate)
        while (currDate.add(1, 'days').diff(lastDate) <= 0) {
          result.push(currDate.clone().format('YYYYMMDD'))
        }
        return result
      }
      for (let date of dateList) {
        usersResult[0].data.push({
          label: date,
          value: usersGroupBy[date] && usersGroupBy[date].users ? usersGroupBy[date].users.reduce((a, b) => a + b) : 0
        })
        viewsResult[0].data.push({
          label: date,
          value: viewsGroupBy[date] && viewsGroupBy[date].views ? viewsGroupBy[date].views.reduce((a, b) => a + b) : 0
        })
      }
      for (let day = 0; day < 7; day++) { // 0(sunday) ~ 6(saturday)
        const dayLocale = `day_of_week_${day}`
        daysResult[0].data.push({
          label: this.locale[dayLocale],
          value: daysGroupBy[day] && daysGroupBy[day].views ? daysGroupBy[day].views.reduce((a, b) => a + b) : 0
        })
      }
      // this.overallData = result
      this.overallUsers = usersResult
      this.overallViews = viewsResult
      this.pageViewDays = daysResult
    },
    setPageView (pageview, pageviewTime) {
      const pageViewResult = pageview ? pageview.rows.map(x => {
        return {
          path: x.dimensionValues[0].value,
          title: x.dimensionValues[1].value,
          // users: x.metricValues[0].value,
          views: x.metricValues[0].value,
          sessions: x.metricValues[1].value,
          sessionDur: x.metricValues[2].value
        }
      }) : []
      let viewTimeResult = [
        {
          name: this.locale.time,
          data: []
        }
      ]
      const viewTimeData = pageviewTime ? pageviewTime.rows.map(x => {
        return {
          time: parseFloat(x.dimensionValues[0].value),
          views: parseInt(x.metricValues[0].value)
        }
      }) : []
      const timeGroupBy = this.groupBy(viewTimeData, 'time', 'views')
      for (let i = 0; i < 24; i++) {
        viewTimeResult[0].data.push({
          label: i,
          value: timeGroupBy[i] && timeGroupBy[i].views ? timeGroupBy[i].views.reduce((a, b) => a + b) : 0
        })
      }
      this.pageViewData = pageViewResult
      this.pageViewTime = viewTimeResult
    },
    async getAnalyticsOverall (from, to) {
      const siteid = localStorage.getItem('siteid')
      // overall:: users data
      const overallDataUsers = {
        from: from,
        to: to,
        dimensions: [
          'date',
          'pagePath'
        ],
        metrics: [
          'totalUsers'
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              {
                filter: {
                  fieldName: 'pagePath',
                  stringFilter: {
                    matchType: 'BEGINS_WITH',
                    value: `/${siteid}/`
                  }
                }
              }
            ]
          }
        }
      }
      // overall:: views data
      const overallDataViews = {
        from: from,
        to: to,
        dimensions: [
          'date',
          'dayOfWeek',
          'pagePath',
          'pageTitle'
        ],
        metrics: [
          'screenPageViews'
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              {
                filter: {
                  fieldName: 'pagePath',
                  stringFilter: {
                    matchType: 'CONTAINS',
                    value: `/${siteid}/`
                  }
                }
              },
              {
                notExpression: {
                  filter: {
                    fieldName: 'pageTitle',
                    stringFilter: {
                      matchType: 'CONTAINS',
                      value: 'enterance'
                    }
                  }
                }
              }
            ]
          }
        }
      }
      // overall dashboard
      this.$store.commit('onLoading', true)
      const overallUsers = await this.$store.dispatch('analytics/getAnalytics', overallDataUsers)
      const overallViews = await this.$store.dispatch('analytics/getAnalytics', overallDataViews)
      this.setOverall(overallUsers, overallViews)
      this.$store.commit('onLoading', false)
    },
    async getAnalyticsPageview (from, to) {
      const siteid = localStorage.getItem('siteid')
      // user pageview data
      const pageViewData = {
        from: from,
        to: to,
        dimensions: [
          'pagePath',
          'pageTitle'
        ],
        metrics: [
          'screenPageViews',
          'sessions',
          'averageSessionDuration'
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              {
                filter: {
                  fieldName: 'pagePath',
                  stringFilter: {
                    matchType: 'CONTAINS',
                    value: `${siteid}/`
                  }
                }
              },
              {
                notExpression: {
                  filter: {
                    fieldName: 'pageTitle',
                    stringFilter: {
                      matchType: 'CONTAINS',
                      value: 'enterance'
                    }
                  }
                }
              }
            ]
          }
        }
      }
      // user active time
      const pageViewTimeData = {
        from: from,
        to: to,
        dimensions: [
          'hour',
          'pagePath',
          'pageTitle'
        ],
        metrics: [
          'screenPageViews'
        ],
        dimensionFilter: {
          andGroup: {
            expressions: [
              {
                filter: {
                  fieldName: 'pagePath',
                  stringFilter: {
                    matchType: 'CONTAINS',
                    value: `${siteid}/`
                  }
                }
              }
            ]
          }
        }
      }
      // pageview dashboard
      this.$store.commit('onLoading', true)
      const pageViews = await this.$store.dispatch('analytics/getAnalytics', pageViewData)
      const pageViewTimes = await this.$store.dispatch('analytics/getAnalytics', pageViewTimeData)
      this.setPageView(pageViews, pageViewTimes)
      this.$store.commit('onLoading', false)
    },
    async getAnalytics (dateFrom, dateTo) {
      const startDate = dateFrom || moment().add(-7, 'days').toISOString()
      const endDate = dateTo || moment().toISOString()
      const from = moment(startDate).format('YYYY-MM-DD')
      const to = moment(endDate).format('YYYY-MM-DD')
      this.filterData = {
        startDate: startDate,
        endDate: endDate
      }
      this.getAnalyticsOverall(from, to)
      this.getAnalyticsPageview(from, to)
    }
  }
}
</script>

<style lang="scss" scoped>
.userChart {
  display: flex;
  margin-bottom: 60px;
  .innerChart {
    flex-basis: calc(50% - 15px);
    margin-right: 15px;
    border: 1px solid $color-lgrey2;
    & + .innerChart {
      margin-right: 0;
      margin-left: 15px;
    }
  }
}
</style>
